import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { Search, CornerDownLeft, X, Users, Shield, LayoutGrid } from "lucide-react";

const CLUB_COLOR = {
  Analistas: "#A855F7", Diplomatas: "#22C55E",
  Sentinelas: "#3B82F6", Exploradores: "#EAB308",
};

const norm = (s = "") => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

/** Header trigger. Multiple triggers may exist; the palette is rendered once. */
export const SearchTrigger = ({ onOpen, compact = false }) => (
  compact ? (
    <button onClick={onOpen} className="text-[#A3A3A3] hover:text-white p-2"
      aria-label="Pesquisar" data-testid="global-search-open">
      <Search className="w-5 h-5" />
    </button>
  ) : (
    <button onClick={onOpen}
      className="flex items-center gap-2 w-full max-w-sm px-3 py-1.5 border border-[#222] bg-[#0F0F0F] text-[#737373] hover:border-[#444] hover:text-[#A3A3A3] transition-colors"
      data-testid="global-search-open">
      <Search className="w-3.5 h-3.5 shrink-0" />
      <span className="text-xs flex-1 text-left">Pesquisar pessoas, equipas, páginas…</span>
      <span className="text-[9px] uppercase tracking-widest border border-[#2A2A2A] px-1.5 py-0.5 hidden lg:inline">
        Ctrl K
      </span>
    </button>
  )
);

/** Command palette: pages + people + teams. Ctrl/Cmd+K anywhere. Render ONCE. */
export const SearchPalette = ({ open, setOpen, pages = [] }) => {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [remote, setRemote] = useState({ people: [], teams: [] });
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [setOpen]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 40);
    else { setQ(""); setRemote({ people: [], teams: [] }); setCursor(0); }
  }, [open]);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setRemote({ people: [], teams: [] }); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/search?q=${encodeURIComponent(term)}`);
        setRemote({ people: r.data.people || [], teams: r.data.teams || [] });
      } catch { setRemote({ people: [], teams: [] }); }
    }, 220);
    return () => clearTimeout(t);
  }, [q]);

  if (!open) return null;

  const term = norm(q.trim());
  const pageHits = term
    ? pages.filter((p) => norm(p.label).includes(term)).slice(0, 6)
    : pages.slice(0, 6);

  const results = [
    ...pageHits.map((p) => ({ type: "page", key: `p-${p.to}`, label: p.label, icon: p.icon, to: p.to })),
    ...remote.people.map((x) => ({
      type: "person", key: `u-${x.id}`, label: x.name, sub: x.email,
      badge: x.kind_label, club: x.club, mbti: x.mbti,
      to: `/directory?q=${encodeURIComponent(x.name || "")}`,
    })),
    ...remote.teams.map((t) => ({
      type: "team", key: `t-${t.id}`, label: (t.name || "").trim(), badge: "Equipa",
      to: "/directory?tab=teams",
    })),
  ];

  const go = (item) => { if (!item) return; setOpen(false); navigate(item.to); };

  const onInputKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setCursor((c) => Math.min(c + 1, results.length - 1)); }
    if (e.key === "ArrowUp") { e.preventDefault(); setCursor((c) => Math.max(c - 1, 0)); }
    if (e.key === "Enter") { e.preventDefault(); go(results[cursor]); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/75 backdrop-blur-sm flex items-start justify-center p-4 pt-[12vh]"
      onClick={() => setOpen(false)} data-testid="global-search-modal">
      <div className="w-full max-w-xl bg-[#0F0F0F] border border-[#2A2A2A] shadow-2xl"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 px-4 py-3 border-b border-[#1F1F1F]">
          <Search className="w-4 h-4 text-[#E50000] shrink-0" />
          <input ref={inputRef} value={q}
            onChange={(e) => { setQ(e.target.value); setCursor(0); }}
            onKeyDown={onInputKey}
            placeholder="Nome, email, KW UID, equipa ou página…"
            className="flex-1 bg-transparent outline-none text-sm placeholder:text-[#555]"
            data-testid="global-search-input" />
          <button onClick={() => setOpen(false)} className="text-[#555] hover:text-white" aria-label="Fechar">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="max-h-[55vh] overflow-y-auto py-1" data-testid="global-search-results">
          {results.length === 0 && (
            <div className="px-4 py-8 text-center text-xs uppercase tracking-widest text-[#555]">
              {q.trim().length < 2 ? "Escreve pelo menos 2 letras" : "Sem resultados"}
            </div>
          )}
          {results.map((r, i) => (
            <button key={r.key} onClick={() => go(r)} onMouseEnter={() => setCursor(i)}
              className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${i === cursor ? "bg-[#1A1A1A]" : "hover:bg-[#151515]"}`}
              data-testid={`global-search-result-${i}`}>
              <span className="w-6 shrink-0 text-[#555]">
                {r.type === "page" && r.icon ? <r.icon className="w-4 h-4" />
                  : r.type === "team" ? <Shield className="w-4 h-4" />
                    : r.type === "person" ? <Users className="w-4 h-4" />
                      : <LayoutGrid className="w-4 h-4" />}
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-bold truncate">{r.label}</span>
                {r.sub && <span className="block text-[10px] text-[#737373] truncate">{r.sub}</span>}
              </span>
              {r.mbti && (
                <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 border shrink-0"
                  style={{ borderColor: CLUB_COLOR[r.club] || "#333", color: CLUB_COLOR[r.club] || "#737373" }}>
                  {r.mbti}
                </span>
              )}
              <span className="text-[9px] uppercase tracking-widest text-[#555] shrink-0">
                {r.type === "page" ? "Página" : r.badge}
              </span>
              {i === cursor && <CornerDownLeft className="w-3 h-3 text-[#E50000] shrink-0" />}
            </button>
          ))}
        </div>

        <div className="px-4 py-2 border-t border-[#1F1F1F] flex items-center gap-4 text-[9px] uppercase tracking-widest text-[#444]">
          <span>↑↓ navegar</span><span>↵ abrir</span><span>Esc fechar</span>
        </div>
      </div>
    </div>
  );
};
